import React, { useEffect, useRef, useState } from "react";
import { Upload, FileText, Trash2, ExternalLink, Loader2, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { openFile } from "@/pages/shared";

function Thumb({ doc }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!doc.content_type?.startsWith("image/")) return;
    let url;
    api.get(`/files/${doc.id}/download`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [doc.id, doc.content_type]);
  return src
    ? <img src={src} alt={doc.original_filename} className="h-12 w-16 object-cover rounded-md border border-border" />
    : <span className="h-12 w-16 grid place-items-center rounded-md border border-border bg-muted"><FileText className="h-5 w-5 text-muted-foreground" /></span>;
}

export default function DocumentsField({ docs, setDocs, guestId }) {
  const { t } = useLang();
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const fileRef = useRef(null);

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("category", "passport");
        if (guestId) fd.append("guest_id", guestId);
        uploaded.push((await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } })).data);
      }
      setDocs([...docs, ...uploaded]);
      toast.success(`${uploaded.length} ${t("docs_uploaded")}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async (d) => {
    try { await api.delete(`/files/${d.id}`); setDocs(docs.filter((x) => x.id !== d.id)); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-2 rounded-lg px-3 py-2 border border-border" style={{ background: "linear-gradient(90deg, var(--hatch) 0 10px, #fff 10px calc(100% - 10px), var(--hatch) calc(100% - 10px) 100%)", "--hatch": "repeating-linear-gradient(135deg, #e0f2fe 0 3px, #ffffff 3px 6px)" }} data-testid="documents-field">
      <Label className="text-[11px] text-muted-foreground">{t("passport_docs")}</Label>
      <div
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); upload(e.dataTransfer.files); }}
        className={`flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed px-3 py-4 text-center transition-colors ${over ? "border-primary bg-secondary" : "border-slate-300 bg-card"}`}
        data-testid="doc-dropzone"
      >
        <input ref={fileRef} type="file" accept="image/*,.pdf" multiple className="hidden" onChange={(e) => upload(e.target.files)} data-testid="doc-file-input" />
        {busy ? <Loader2 className="h-6 w-6 animate-spin text-primary" /> : <ImagePlus className="h-6 w-6 text-primary" />}
        <p className="text-xs text-muted-foreground">{t("drop_photos")}</p>
        <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="doc-file-button">
          <Upload className="h-4 w-4 mr-1" />{t("choose_files")}
        </Button>
      </div>
      {docs.length > 0 && (
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="documents-list">
          {docs.map((d) => (
            <li key={d.id} className="flex items-center gap-2 rounded-lg border border-border bg-card p-1.5" data-testid={`doc-${d.id}`}>
              <Thumb doc={d} />
              <span className="flex-1 min-w-0 text-xs truncate" title={d.original_filename}>{d.original_filename}</span>
              <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => openFile(api, d.id)} title={t("open")}><ExternalLink className="h-4 w-4" /></Button>
              <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => remove(d)} data-testid={`doc-remove-${d.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
